// Service pour envoyer des emails directement via l'API Resend depuis le frontend

export interface ResendEmailData {
  receiverEmail: string;
  receiverUsername: string;
  senderUsername: string;
  tradeDetails: {
    senderAssets: Array<{ name: string; type: 'player' | 'goalie' | 'pick'; salary?: number }>;
    receiverAssets: Array<{ name: string; type: 'player' | 'goalie' | 'pick'; salary?: number }>;
  };
  leagueName: string;
}

export interface JoinRequestEmailData {
  commissionerEmail: string;
  commissionerUsername: string;
  requesterUsername: string;
  leagueName: string;
}

export interface TradeResponseEmailData {
  senderEmail: string;
  senderUsername: string;
  receiverUsername: string;
  isAccepted: boolean;
  leagueName: string;
  senderAssets?: Array<{ name: string; type: 'player' | 'goalie' | 'pick'; salary?: number }>;
  receiverAssets?: Array<{ name: string; type: 'player' | 'goalie' | 'pick'; salary?: number }>;
}

export interface JoinRequestDecisionEmailData {
  userEmail: string;
  username: string;
  leagueName: string;
  isApproved: boolean;
}

export interface DraftTurnEmailData {
  userEmail: string;
  username: string;
  leagueName: string;
  pickNumber: number;
  type?: 'current' | 'upcoming';
}

export interface LeagueInvitationEmailData {
  inviteeEmail: string;
  leagueName: string;
  commissionerUsername: string;
  leagueId: number;
}

export interface TradeFinalizedEmailData {
  userEmail: string;
  username: string;
  otherUsername: string;
  leagueName: string;
  assetsReceived: Array<{ name: string; type: 'player' | 'goalie' | 'pick' }>;
  assetsSent: Array<{ name: string; type: 'player' | 'goalie' | 'pick' }>;
}

export interface AdminNewUserEmailData {
  username: string;
  email: string;
}

export interface AdminNewLeagueEmailData {
  leagueName: string;
  creatorUsername: string;
  leagueType: string;
}

class ResendEmailService {
  private supabaseUrl: string;
  private supabaseAnonKey: string;
  private fromEmail: string;
  private frontendUrl: string;
  private emailsDisabled: boolean;

  constructor() {
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://ezjthfrjbqwpxrksijml.supabase.co';
    this.supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
    this.fromEmail = import.meta.env.VITE_FROM_EMAIL || 'notification@hockeydynasty.net';
    this.frontendUrl = import.meta.env.VITE_FRONTEND_URL || window.location.origin;
    this.emailsDisabled = import.meta.env.VITE_DISABLE_EMAILS === 'true';

    if (!this.supabaseAnonKey) {
      console.warn('VITE_SUPABASE_ANON_KEY not configured. Email notifications will be disabled.');
    }

    if (this.emailsDisabled) {
      console.warn('📧 [DISABLED] Emails are disabled via VITE_DISABLE_EMAILS environment variable');
    }
  }

  private async sendEmail(payload: {
    to: string[];
    subject: string;
    html: string;
    text: string;
    tags?: Array<{ name: string; value: string }>;
  }): Promise<{ success: boolean; id?: string; error?: string }> {
    if (this.emailsDisabled) {
      console.log('📧 [DISABLED] Email skipped - emails disabled via VITE_DISABLE_EMAILS');
      return { success: true };
    }

    if (!this.supabaseAnonKey) {
      console.warn('Supabase anon key not configured, skipping email notification');
      return { success: false, error: 'Supabase configuration missing' };
    }

    try {
      const edgeFunctionUrl = `${this.supabaseUrl}/functions/v1/send-email`;
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.supabaseAnonKey,
          'Authorization': `Bearer ${this.supabaseAnonKey}`,
        },
        body: JSON.stringify({
          from: this.fromEmail,
          to: payload.to,
          subject: payload.subject,
          html: payload.html,
          text: payload.text,
          tags: payload.tags,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('send-email Edge Function error:', response.status, result);
        return { success: false, error: result.error || `HTTP ${response.status}` };
      }

      console.log('✅ Email sent successfully via Resend:', result.id);
      return { success: true, id: result.id };
    } catch (error: any) {
      console.error('❌ Error sending email via Edge Function:', error);
      return { success: false, error: error.message || 'Unknown error occurred' };
    }
  }

  private generateTradeEmailContent(data: ResendEmailData): { html: string; text: string } {
    const getAssetTypeLabel = (type: string) => {
      switch (type) {
        case 'goalie': return 'Gardien';
        case 'pick': return 'Choix de draft';
        default: return 'Joueur';
      }
    };

    const senderAssetsText = data.tradeDetails.senderAssets
      .map(asset => `${asset.name} (${getAssetTypeLabel(asset.type)})${asset.salary ? ` - $${asset.salary.toLocaleString()}` : ''}`)
      .join(', ');

    const receiverAssetsText = data.tradeDetails.receiverAssets
      .map(asset => `${asset.name} (${getAssetTypeLabel(asset.type)})${asset.salary ? ` - $${asset.salary.toLocaleString()}` : ''}`)
      .join(', ');

    const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 30px; padding: 20px 0; border-bottom: 3px solid #2563eb;">
          <h1 style="color: #2563eb; margin: 0; font-size: 28px; font-weight: bold;">
            🏒 HockeyDynasty
          </h1>
          <p style="color: #6b7280; margin: 5px 0 0 0; font-size: 14px;">Notification Officielle du Système</p>
        </div>

        <!-- Message principal -->
        <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <p style="margin: 0 0 10px 0; font-size: 16px;"><strong>Salut ${data.receiverUsername}!</strong></p>
          <p style="margin: 0; color: #374151; line-height: 1.5;">
            Vous avez reçu une nouvelle proposition d'échange de <strong>${data.senderUsername}</strong>
            dans la ligue <strong>${data.leagueName}</strong>.
          </p>
        </div>

        <!-- Détails de l'échange -->
        <div style="background-color: #ffffff; border: 2px solid #e2e8f0; border-radius: 12px; overflow: hidden; margin: 25px 0; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);">
          <div style="background-color: #f1f5f9; padding: 15px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="color: #1e40af; margin: 0; font-size: 18px; text-align: center;">
              📋 Détails de la Transaction
            </h3>
          </div>

          <div style="padding: 20px;">
            <!-- Section Offre -->
            <div style="margin-bottom: 20px; padding: 15px; background-color: #f0fdf4; border-radius: 8px; border-left: 4px solid #059669;">
              <h4 style="color: #059669; margin: 0 0 10px 0; font-size: 16px;">
                💰 ${data.senderUsername} offre:
              </h4>
              <div style="background-color: #ffffff; padding: 12px; border-radius: 6px; border: 1px solid #dcfce7; color: #374151; font-size: 14px; line-height: 1.4;">
                ${senderAssetsText || '<em style="color: #6b7280;">Aucun actif</em>'}
              </div>
            </div>

            <!-- Section Demande -->
            <div style="margin-bottom: 20px; padding: 15px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #dc2626;">
              <h4 style="color: #dc2626; margin: 0 0 10px 0; font-size: 16px;">
                🎯 ${data.senderUsername} demande:
              </h4>
              <div style="background-color: #ffffff; padding: 12px; border-radius: 6px; border: 1px solid #fecaca; color: #374151; font-size: 14px; line-height: 1.4;">
                ${receiverAssetsText || '<em style="color: #6b7280;">Aucun actif</em>'}
              </div>
            </div>
          </div>
        </div>

        <!-- CTA -->
        <div style="text-align: center; margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-radius: 12px; border: 1px solid #bfdbfe;">
          <p style="margin: 0 0 15px 0; font-size: 16px; color: #1e40af; font-weight: 600;">
            ⏰ Action requise de votre part
          </p>
          <p style="margin: 0 0 20px 0; color: #374151; font-size: 14px; line-height: 1.5;">
            Connectez-vous à votre compte pour <strong>accepter</strong> ou <strong>refuser</strong> cette proposition d'échange.
          </p>
          <a href="${this.frontendUrl}/messages"
             style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);">
            📬 Voir l'Échange
          </a>
          <p style="margin: 15px 0 0 0; font-size: 12px; color: #6b7280;">
            Lien direct: ${this.frontendUrl}/messages
          </p>
        </div>

        <!-- Footer -->
        <div style="border-top: 2px solid #e2e8f0; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 12px; text-align: center;">
          <p style="margin: 0 0 10px 0; line-height: 1.4;">
            Ceci est une notification officielle de <strong>HockeyDynasty</strong>.<br>
            Cette adresse email est associée à votre compte ${data.receiverEmail}.
          </p>
          <p style="margin: 0;">
            Pour modifier vos préférences de notification,
            <a href="${this.frontendUrl}/profile/notifications" style="color: #2563eb; text-decoration: none;">cliquez ici</a>.
          </p>
        </div>
      </div>
    `;

    const text = `
Nouvelle Proposition d'Échange - ${data.leagueName}

Salut ${data.receiverUsername}!

Vous avez reçu une nouvelle proposition d'échange de ${data.senderUsername} dans la ligue ${data.leagueName}.

${data.senderUsername} offre: ${senderAssetsText || 'Aucun actif'}
${data.senderUsername} demande: ${receiverAssetsText || 'Aucun actif'}

Connectez-vous à votre compte pour accepter ou refuser cette proposition:
${this.frontendUrl}/messages

Ceci est un email automatique du système Dynasty Hockey Pool.
    `;

    return { html, text };
  }

  private generateJoinRequestEmailContent(data: JoinRequestEmailData): { html: string; text: string } {
    const html = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Nouvelle Demande d'Adhésion - ${data.leagueName}</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; background-color: #f8f9fa; }
        .container { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
        .header { background: linear-gradient(135deg, #007bff, #0056b3); color: white; padding: 30px; text-align: center; }
        .header h1 { margin: 0; font-size: 24px; font-weight: 600; }
        .content { padding: 30px; }
        .greeting { font-size: 18px; color: #2c3e50; margin-bottom: 20px; font-weight: 500; }
        .message { font-size: 16px; color: #34495e; line-height: 1.6; margin-bottom: 25px; }
        .highlight { background-color: #e8f4fd; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #007bff; }
        .highlight strong { color: #007bff; }
        .cta-button { display: inline-block; background: linear-gradient(135deg, #28a745, #20c997); color: white !important; text-decoration: none; padding: 12px 30px; border-radius: 6px; font-weight: 600; margin: 20px 0; text-align: center; }
        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; border-top: 1px solid #dee2e6; color: #6c757d; font-size: 14px; }
        .brand { color: #007bff; font-weight: 600; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🏒 Dynasty Hockey Pool</h1>
            <p style="margin: 5px 0 0 0; opacity: 0.9;">Nouvelle Demande d'Adhésion</p>
        </div>

        <div class="content">
            <div class="greeting">Salut ${data.commissionerUsername}!</div>

            <div class="message">
                Vous avez reçu une nouvelle demande d'adhésion pour votre ligue <strong>${data.leagueName}</strong>.
            </div>

            <div class="highlight">
                <strong>👤 Demandeur :</strong> ${data.requesterUsername}<br>
                <strong>🏆 Ligue :</strong> ${data.leagueName}
            </div>

            <div class="message">
                En tant que commissaire de la ligue, vous pouvez accepter ou refuser cette demande d'adhésion.
            </div>

            <div style="text-align: center;">
                <a href="${this.frontendUrl}/messages" class="cta-button">
                    📋 Gérer les Demandes d'Adhésion
                </a>
            </div>

            <div class="message" style="font-size: 14px; color: #6c757d; margin-top: 30px;">
                Connectez-vous à votre compte Dynasty Hockey Pool pour traiter cette demande.
            </div>
        </div>

        <div class="footer">
            <p style="margin: 0;">Ceci est un email automatique du système <span class="brand">Dynasty Hockey Pool</span>.</p>
            <p style="margin: 5px 0 0 0;">Merci de ne pas répondre à cet email.</p>
        </div>
    </div>
</body>
</html>
    `;

    const text = `
Nouvelle Demande d'Adhésion - ${data.leagueName}

Salut ${data.commissionerUsername}!

Vous avez reçu une nouvelle demande d'adhésion pour votre ligue ${data.leagueName}.

Demandeur: ${data.requesterUsername}
Ligue: ${data.leagueName}

En tant que commissaire de la ligue, vous pouvez accepter ou refuser cette demande d'adhésion.

Connectez-vous à votre compte pour traiter cette demande:
${this.frontendUrl}/messages

Ceci est un email automatique du système Dynasty Hockey Pool.
    `;

    return { html, text };
  }

  async sendTradeNotification(data: ResendEmailData): Promise<{ success: boolean; error?: string }> {
    console.log('📧 Sending trade notification email via Resend...', { to: data.receiverEmail });
    const { html, text } = this.generateTradeEmailContent(data);
    return this.sendEmail({
      to: [data.receiverEmail],
      subject: `[${data.leagueName}] Nouvelle proposition d'échange de ${data.senderUsername}`,
      html,
      text,
      tags: [{ name: 'category', value: 'trade-notification' }],
    });
  }

  async sendJoinRequestNotification(data: JoinRequestEmailData): Promise<{ success: boolean; error?: string }> {
    console.log('📧 Sending join request notification email via Resend...', { to: data.commissionerEmail });
    const { html, text } = this.generateJoinRequestEmailContent(data);
    return this.sendEmail({
      to: [data.commissionerEmail],
      subject: `[${data.leagueName}] Nouvelle demande d'adhésion de ${data.requesterUsername}`,
      html,
      text,
      tags: [{ name: 'category', value: 'join-request' }],
    });
  }

  private generateTradeResponseEmailContent(data: TradeResponseEmailData): { html: string; text: string } {
    const statusText = data.isAccepted ? 'accepté' : 'refusé';
    const statusColor = data.isAccepted ? '#059669' : '#dc2626';
    const statusIcon = data.isAccepted ? '✅' : '❌';

    // Helper to render assets list
    const renderAssetsHtml = (assets?: Array<{ name: string; type: string; salary?: number }>) => {
      if (!assets || assets.length === 0) return '<em style="color: #6b7280;">Aucun actif</em>';
      return `<ul style="margin:0;padding-left:18px;color:#374151;font-size:14px;">${assets.map(a => `<li style="margin:6px 0">${a.name} ${a.salary ? ` - $${a.salary.toLocaleString()}` : ''}</li>`).join('')}</ul>`;
    };

    // If refused, provide a more detailed, explicit message with assets + CTA to propose a counter-offer
    if (!data.isAccepted) {
      const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
        <div style="text-align: center; margin-bottom: 30px; padding: 20px 0; border-bottom: 3px solid #2563eb;">
          <h1 style="color: #2563eb; margin: 0; font-size: 28px; font-weight: bold;">🏒 HockeyDynasty</h1>
          <p style="color: #6b7280; margin: 5px 0 0 0; font-size: 14px;">Notification Officielle du Système</p>
        </div>
        <div style="background-color: #fff7f7; padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid #fee2e2;">
          <p style="margin: 0 0 10px 0; font-size: 16px;"><strong>Salut ${data.senderUsername}!</strong></p>
          <p style="margin: 0; color: #374151; line-height: 1.5;">
            Votre proposition d'échange dans la ligue <strong>${data.leagueName}</strong> a été
            <strong style="color: ${statusColor};">${statusIcon} ${statusText}</strong>
            par <strong>${data.receiverUsername}</strong>.
          </p>
        </div>

        <div style="padding: 0 0 10px 0;">
          <h4 style="margin: 0 0 6px 0; color: #9a1f2b;">Détails de l'échange</h4>
          <div style="display:flex;gap:12px;flex-wrap:wrap;">
            <div style="flex:1;min-width:220px;padding:12px;background:#fff;border-radius:8px;border:1px solid #fee2e2;">
              <strong style="color:#dc2626">💰 Vous proposiez :</strong>
              ${renderAssetsHtml(data.senderAssets)}
            </div>
            <div style="flex:1;min-width:220px;padding:12px;background:#fff;border-radius:8px;border:1px solid #fee2e2;">
              <strong style="color:#059669">🎯 Vous demandiez :</strong>
              ${renderAssetsHtml(data.receiverAssets)}
            </div>
          </div>
        </div>

        <div style="text-align:center;margin:25px 0;padding:20px;background:linear-gradient(135deg,#fff7f7 0%,#fff1f1 100%);border-radius:12px;">
          <p style="margin:0 0 12px 0;color:#374151;font-size:14px;">Si vous le souhaitez, proposez une contre-offre ou modifiez votre proposition.</p>
          <a href="${this.frontendUrl}/messages" style="background:#dc2626;color:#fff;padding:12px 20px;border-radius:8px;text-decoration:none;font-weight:600;">🔁 Proposer une contre-offre</a>
        </div>

        <div style="border-top: 2px solid #e2e8f0; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 12px; text-align: center;">
          <p style="margin: 0;">Ceci est une notification automatique de <strong>HockeyDynasty</strong>.</p>
        </div>
      </div>
    `;

      const textLines = [] as string[];
      textLines.push(`Votre proposition d'échange dans la ligue ${data.leagueName} a été refusée par ${data.receiverUsername}.`);
      if (data.senderAssets && data.senderAssets.length > 0) {
        textLines.push('\nVous proposiez:');
        data.senderAssets.forEach(a => textLines.push(`- ${a.name}${a.salary ? ` - $${a.salary.toLocaleString()}` : ''}`));
      }
      if (data.receiverAssets && data.receiverAssets.length > 0) {
        textLines.push('\nVous demandiez:');
        data.receiverAssets.forEach(a => textLines.push(`- ${a.name}`));
      }
      textLines.push('\nProposez une contre-offre: ' + `${this.frontendUrl}/messages`);

      return { html, text: textLines.join('\n') };
    }

    // Accepted - keep concise but include assets as confirmation
    const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
        <div style="text-align: center; margin-bottom: 30px; padding: 20px 0; border-bottom: 3px solid #2563eb;">
          <h1 style="color: #2563eb; margin: 0; font-size: 28px; font-weight: bold;">🏒 HockeyDynasty</h1>
          <p style="color: #6b7280; margin: 5px 0 0 0; font-size: 14px;">Notification Officielle du Système</p>
        </div>
        <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <p style="margin: 0 0 10px 0; font-size: 16px;"><strong>Salut ${data.senderUsername}!</strong></p>
          <p style="margin: 0; color: #374151; line-height: 1.5;">
            Votre proposition d'échange dans la ligue <strong>${data.leagueName}</strong> a été
            <strong style="color: ${statusColor};">${statusIcon} ${statusText}</strong>
            par <strong>${data.receiverUsername}</strong>.
          </p>
        </div>

        <div style="text-align: center; margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-radius: 12px; border: 1px solid #bfdbfe;">
          <a href="${this.frontendUrl}/messages"
             style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);">
            📬 Voir mes échanges
          </a>
        </div>

        <div style="border-top: 2px solid #e2e8f0; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 12px; text-align: center;">
          <p style="margin: 0;">Ceci est une notification automatique de <strong>HockeyDynasty</strong>.</p>
        </div>
      </div>
    `;

    const text = `Salut ${data.senderUsername}!\n\nVotre proposition d'échange dans la ligue ${data.leagueName} a été ${statusText} par ${data.receiverUsername}.\n\nVoir vos échanges: ${this.frontendUrl}/messages`;
    return { html, text };
  }

  private generateJoinRequestDecisionEmailContent(data: JoinRequestDecisionEmailData): { html: string; text: string } {
    const statusText = data.isApproved ? 'acceptée' : 'refusée';
    const statusColor = data.isApproved ? '#059669' : '#dc2626';
    const statusIcon = data.isApproved ? '✅' : '❌';
    const ctaHtml = data.isApproved
      ? `<a href="${this.frontendUrl}" style="background: linear-gradient(135deg, #059669 0%, #047857 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px;">🏒 Accéder à la ligue</a>`
      : '';

    const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
        <div style="text-align: center; margin-bottom: 30px; padding: 20px 0; border-bottom: 3px solid #2563eb;">
          <h1 style="color: #2563eb; margin: 0; font-size: 28px; font-weight: bold;">🏒 HockeyDynasty</h1>
        </div>
        <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <p style="margin: 0 0 10px 0; font-size: 16px;"><strong>Salut ${data.username}!</strong></p>
          <p style="margin: 0; color: #374151; line-height: 1.5;">
            Votre demande d'adhésion à la ligue <strong>${data.leagueName}</strong> a été
            <strong style="color: ${statusColor};">${statusIcon} ${statusText}</strong>.
          </p>
        </div>
        ${data.isApproved ? `<div style="text-align: center; margin: 30px 0;">${ctaHtml}</div>` : ''}
        <div style="border-top: 2px solid #e2e8f0; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 12px; text-align: center;">
          <p style="margin: 0;">Ceci est une notification automatique de <strong>HockeyDynasty</strong>.</p>
        </div>
      </div>
    `;

    const text = `Salut ${data.username}!\n\nVotre demande d'adhésion à la ligue ${data.leagueName} a été ${statusText}.\n\n${data.isApproved ? `Accéder à la ligue: ${this.frontendUrl}` : ''}`;
    return { html, text };
  }

  private generateDraftTurnEmailContent(data: DraftTurnEmailData): { html: string; text: string } {
    if (data.type === 'upcoming') {
      const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
        <div style="text-align: center; margin-bottom: 30px; padding: 20px 0; border-bottom: 3px solid #2563eb;">
          <h1 style="color: #2563eb; margin: 0; font-size: 28px; font-weight: bold;">🏒 HockeyDynasty</h1>
          <p style="color: #6b7280; margin: 5px 0 0 0; font-size: 14px;">Repêchage en direct</p>
        </div>
        <div style="background-color: #eff6ff; border: 2px solid #2563eb; padding: 20px; border-radius: 8px; margin: 20px 0; text-align: center;">
          <p style="margin: 0 0 10px 0; font-size: 24px;">🔜</p>
          <p style="margin: 0 0 10px 0; font-size: 20px; font-weight: bold; color: #1e40af;"><strong>Vous sélectionnez bientôt!</strong></p>
          <p style="margin: 0; color: #374151; font-size: 16px;">
            Salut <strong>${data.username}</strong>, vous êtes le prochain à choisir (choix #${data.pickNumber}) dans la ligue <strong>${data.leagueName}</strong>. Préparez-vous!
          </p>
        </div>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${this.frontendUrl}/draft"
             style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(37, 99, 235, 0.4);">
            🏒 Préparer ma liste de priorités
          </a>
        </div>
        <div style="border-top: 2px solid #e2e8f0; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 12px; text-align: center;">
          <p style="margin: 0;">Ceci est une notification automatique de <strong>HockeyDynasty</strong>.</p>
        </div>
      </div>
      `;
      const text = `Vous sélectionnez bientôt!\n\nSalut ${data.username}, vous êtes prochain (choix #${data.pickNumber}) dans la ligue ${data.leagueName}. Préparez-vous!\n\nPréparer votre liste: ${this.frontendUrl}/draft`;
      return { html, text };
    }

    const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
        <div style="text-align: center; margin-bottom: 30px; padding: 20px 0; border-bottom: 3px solid #2563eb;">
          <h1 style="color: #2563eb; margin: 0; font-size: 28px; font-weight: bold;">🏒 HockeyDynasty</h1>
          <p style="color: #6b7280; margin: 5px 0 0 0; font-size: 14px;">Repêchage en direct</p>
        </div>
        <div style="background-color: #fefce8; border: 2px solid #eab308; padding: 20px; border-radius: 8px; margin: 20px 0; text-align: center;">
          <p style="margin: 0 0 10px 0; font-size: 24px;">⏰</p>
          <p style="margin: 0 0 10px 0; font-size: 20px; font-weight: bold; color: #854d0e;"><strong>C'est votre tour!</strong></p>
          <p style="margin: 0; color: #374151; font-size: 16px;">
            Salut <strong>${data.username}</strong>, c'est votre tour de sélectionner (choix #${data.pickNumber}) dans la ligue <strong>${data.leagueName}</strong>.
          </p>
        </div>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${this.frontendUrl}/draft"
             style="background: linear-gradient(135deg, #eab308 0%, #ca8a04 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(234, 179, 8, 0.4);">
            🏒 Faire mon choix maintenant
          </a>
        </div>
        <div style="border-top: 2px solid #e2e8f0; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 12px; text-align: center;">
          <p style="margin: 0;">Ceci est une notification automatique de <strong>HockeyDynasty</strong>.</p>
        </div>
      </div>
    `;

    const text = `C'est votre tour de sélectionner!\n\nSalut ${data.username}, c'est votre tour (choix #${data.pickNumber}) dans la ligue ${data.leagueName}.\n\nFaire votre choix: ${this.frontendUrl}/draft`;
    return { html, text };
  }

  private generateLeagueInvitationEmailContent(data: LeagueInvitationEmailData): { html: string; text: string } {
    const joinUrl = `${this.frontendUrl}/auth?invite_league=${data.leagueId}`;

    const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
        <div style="text-align: center; margin-bottom: 30px; padding: 20px 0; border-bottom: 3px solid #2563eb;">
          <h1 style="color: #2563eb; margin: 0; font-size: 28px; font-weight: bold;">🏒 HockeyDynasty</h1>
          <p style="color: #6b7280; margin: 5px 0 0 0; font-size: 14px;">Invitation à rejoindre une ligue</p>
        </div>
        <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <p style="margin: 0 0 10px 0; font-size: 16px;">Bonjour!</p>
          <p style="margin: 0; color: #374151; line-height: 1.5;">
            <strong>${data.commissionerUsername}</strong> vous invite à rejoindre la ligue
            <strong>${data.leagueName}</strong> sur HockeyDynasty, la plateforme de pool de hockey dynamique!
          </p>
        </div>
        <div style="text-align: center; margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-radius: 12px; border: 1px solid #bfdbfe;">
          <p style="margin: 0 0 15px 0; font-size: 16px; color: #1e40af; font-weight: 600;">Rejoignez la ligue gratuitement!</p>
          <a href="${joinUrl}"
             style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);">
            🏒 Rejoindre la ligue
          </a>
          <p style="margin: 15px 0 0 0; font-size: 12px; color: #6b7280;">Lien direct: ${joinUrl}</p>
        </div>
        <div style="border-top: 2px solid #e2e8f0; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 12px; text-align: center;">
          <p style="margin: 0;">Ceci est une invitation envoyée via <strong>HockeyDynasty</strong>.</p>
        </div>
      </div>
    `;

    const text = `Bonjour!\n\n${data.commissionerUsername} vous invite à rejoindre la ligue ${data.leagueName} sur HockeyDynasty.\n\nCliquez ici pour rejoindre: ${joinUrl}`;
    return { html, text };
  }

  async sendTradeResponseNotification(data: TradeResponseEmailData): Promise<{ success: boolean; error?: string }> {
    const { html, text } = this.generateTradeResponseEmailContent(data);
    const statusLabel = data.isAccepted ? 'accepté' : 'refusé';
    // Use a clearer subject for refusals including both teams
    const subject = data.isAccepted
      ? `[${data.leagueName}] Votre échange a été ${statusLabel} par ${data.receiverUsername}`
      : `[${data.leagueName}] Échange refusé — ${data.senderUsername} → ${data.receiverUsername}`;

    return this.sendEmail({
      to: [data.senderEmail],
      subject,
      html,
      text,
      tags: [{ name: 'category', value: 'trade-response' }],
    });
  }

  async sendJoinRequestDecisionNotification(data: JoinRequestDecisionEmailData): Promise<{ success: boolean; error?: string }> {
    const { html, text } = this.generateJoinRequestDecisionEmailContent(data);
    const statusLabel = data.isApproved ? 'acceptée' : 'refusée';
    return this.sendEmail({
      to: [data.userEmail],
      subject: `[${data.leagueName}] Votre demande d'adhésion a été ${statusLabel}`,
      html,
      text,
      tags: [{ name: 'category', value: 'join-request-decision' }],
    });
  }

  async sendDraftTurnNotification(data: DraftTurnEmailData): Promise<{ success: boolean; error?: string }> {
    const { html, text } = this.generateDraftTurnEmailContent(data);
    const subject = data.type === 'upcoming'
      ? `[${data.leagueName}] Vous sélectionnez bientôt! (Choix #${data.pickNumber})`
      : `[${data.leagueName}] C'est votre tour de sélectionner! (Choix #${data.pickNumber})`;
    return this.sendEmail({
      to: [data.userEmail],
      subject,
      html,
      text,
      tags: [{ name: 'category', value: 'draft-turn' }],
    });
  }

  async sendLeagueInvitationEmail(data: LeagueInvitationEmailData): Promise<{ success: boolean; error?: string }> {
    const { html, text } = this.generateLeagueInvitationEmailContent(data);
    return this.sendEmail({
      to: [data.inviteeEmail],
      subject: `[HockeyDynasty] ${data.commissionerUsername} vous invite à rejoindre ${data.leagueName}`,
      html,
      text,
      tags: [{ name: 'category', value: 'league-invitation' }],
    });
  }

  private generateTradeFinalizedEmailContent(data: TradeFinalizedEmailData): { html: string; text: string } {
    const assetLine = (a: { name: string; type: string }) => {
      const icon = a.type === 'pick' ? '🎟️' : a.type === 'goalie' ? '🧤' : '🏒';
      return `<li style="margin:4px 0">${icon} ${a.name}</li>`;
    };
    const html = `
      <div style="font-family:Arial,sans-serif;max-width:560px;margin:0 auto;background:#1a1a2e;color:#e2e8f0;border-radius:12px;overflow:hidden">
        <div style="background:linear-gradient(135deg,#1e3a5f,#2563eb);padding:28px 32px;text-align:center">
          <h1 style="margin:0;font-size:22px;color:#fff">🤝 Échange Officiel Complété!</h1>
          <p style="margin:6px 0 0;color:#93c5fd;font-size:14px">${data.leagueName}</p>
        </div>
        <div style="padding:28px 32px">
          <p>Bonjour <strong>${data.username}</strong>,</p>
          <p>Votre échange avec <strong>${data.otherUsername}</strong> est maintenant <strong style="color:#22c55e">officiel</strong>!</p>
          <table style="width:100%;border-collapse:separate;border-spacing:8px;margin:20px 0">
            <tr>
              <td style="background:#0f2942;border-radius:8px;padding:16px;vertical-align:top;width:50%">
                <p style="margin:0 0 8px;color:#22c55e;font-weight:bold;font-size:13px">✅ VOUS RECEVEZ</p>
                <ul style="margin:0;padding-left:20px;font-size:14px">${data.assetsReceived.map(assetLine).join('')}</ul>
              </td>
              <td style="background:#0f2942;border-radius:8px;padding:16px;vertical-align:top;width:50%">
                <p style="margin:0 0 8px;color:#ef4444;font-weight:bold;font-size:13px">📤 VOUS ENVOYEZ</p>
                <ul style="margin:0;padding-left:20px;font-size:14px">${data.assetsSent.map(assetLine).join('')}</ul>
              </td>
            </tr>
          </table>
          <p style="color:#94a3b8;font-size:13px">Les joueurs ont été transférés dans votre effectif.</p>
          <div style="text-align:center;margin-top:20px">
            <a href="${this.frontendUrl}/dashboard" style="background:#2563eb;color:#fff;text-decoration:none;padding:10px 24px;border-radius:6px;font-size:14px;font-weight:bold">Voir mon effectif</a>
          </div>
        </div>
        <div style="border-top:2px solid #2d3748;padding:16px 32px;color:#6b7280;font-size:12px;text-align:center">
          <p style="margin:0">HockeyDynasty — ${data.leagueName}</p>
        </div>
      </div>`;
    const receivedText = data.assetsReceived.map(a => `- ${a.name}`).join('\n');
    const sentText = data.assetsSent.map(a => `- ${a.name}`).join('\n');
    const text = `Bonjour ${data.username},\n\nVotre échange avec ${data.otherUsername} dans la ligue ${data.leagueName} est officiel!\n\nVous recevez:\n${receivedText}\n\nVous envoyez:\n${sentText}\n\nLes joueurs ont été transférés dans votre effectif.\n\nHockeyDynasty`;
    return { html, text };
  }

  async sendTradeFinalizedNotification(data: TradeFinalizedEmailData): Promise<{ success: boolean; error?: string }> {
    const { html, text } = this.generateTradeFinalizedEmailContent(data);
    return this.sendEmail({
      to: [data.userEmail],
      subject: `[${data.leagueName}] Échange officiel complété avec ${data.otherUsername}`,
      html,
      text,
      tags: [{ name: 'category', value: 'trade-finalized' }],
    });
  }

  private generateAdminNewUserEmailContent(data: AdminNewUserEmailData): { html: string; text: string } {
    const now = new Date().toLocaleString('fr-CA', { timeZone: 'America/Montreal' });
    const html = `
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;max-width:600px;margin:0 auto;background:#0f172a;color:#e2e8f0;border-radius:12px;overflow:hidden">
        <div style="background:linear-gradient(135deg,#059669,#047857);padding:24px 32px;text-align:center">
          <h1 style="margin:0;font-size:22px;color:#fff">🏒 HockeyDynasty</h1>
          <p style="margin:6px 0 0;color:#a7f3d0;font-size:14px">👤 Nouvel utilisateur inscrit</p>
        </div>
        <div style="padding:24px 32px">
          <p style="font-size:16px;margin:0 0 16px">Un nouvel utilisateur vient de créer un compte.</p>
          <table style="width:100%;border-collapse:collapse;background:#1e293b;border-radius:8px;overflow:hidden">
            <tr>
              <td style="padding:12px 16px;color:#94a3b8;font-size:14px;border-bottom:1px solid #2d3748;width:40%">Nom d'utilisateur</td>
              <td style="padding:12px 16px;font-weight:bold;border-bottom:1px solid #2d3748"><strong>${data.username}</strong></td>
            </tr>
            <tr>
              <td style="padding:12px 16px;color:#94a3b8;font-size:14px">Email</td>
              <td style="padding:12px 16px;font-weight:bold">${data.email}</td>
            </tr>
          </table>
          <p style="color:#64748b;font-size:13px;margin-top:16px">Date d'inscription: ${now}</p>
        </div>
        <div style="border-top:1px solid #1e293b;padding:14px 32px;color:#475569;font-size:12px;text-align:center">
          <p style="margin:0">HockeyDynasty — Notification administrateur</p>
        </div>
      </div>`;
    const text = `Nouvel utilisateur inscrit sur HockeyDynasty\n\nNom d'utilisateur: ${data.username}\nEmail: ${data.email}\nDate: ${now}`;
    return { html, text };
  }

  private generateAdminNewLeagueEmailContent(data: AdminNewLeagueEmailData): { html: string; text: string } {
    const now = new Date().toLocaleString('fr-CA', { timeZone: 'America/Montreal' });
    const html = `
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;max-width:600px;margin:0 auto;background:#0f172a;color:#e2e8f0;border-radius:12px;overflow:hidden">
        <div style="background:linear-gradient(135deg,#d97706,#b45309);padding:24px 32px;text-align:center">
          <h1 style="margin:0;font-size:22px;color:#fff">🏒 HockeyDynasty</h1>
          <p style="margin:6px 0 0;color:#fde68a;font-size:14px">🏆 Nouvelle ligue créée</p>
        </div>
        <div style="padding:24px 32px">
          <p style="font-size:16px;margin:0 0 16px">Une nouvelle ligue vient d'être créée sur la plateforme.</p>
          <table style="width:100%;border-collapse:collapse;background:#1e293b;border-radius:8px;overflow:hidden">
            <tr>
              <td style="padding:12px 16px;color:#94a3b8;font-size:14px;border-bottom:1px solid #2d3748;width:40%">Nom de la ligue</td>
              <td style="padding:12px 16px;font-weight:bold;border-bottom:1px solid #2d3748"><strong>${data.leagueName}</strong></td>
            </tr>
            <tr>
              <td style="padding:12px 16px;color:#94a3b8;font-size:14px;border-bottom:1px solid #2d3748">Créateur</td>
              <td style="padding:12px 16px;font-weight:bold;border-bottom:1px solid #2d3748">${data.creatorUsername}</td>
            </tr>
            <tr>
              <td style="padding:12px 16px;color:#94a3b8;font-size:14px">Type</td>
              <td style="padding:12px 16px;font-weight:bold">${data.leagueType}</td>
            </tr>
          </table>
          <p style="color:#64748b;font-size:13px;margin-top:16px">Date de création: ${now}</p>
        </div>
        <div style="border-top:1px solid #1e293b;padding:14px 32px;color:#475569;font-size:12px;text-align:center">
          <p style="margin:0">HockeyDynasty — Notification administrateur</p>
        </div>
      </div>`;
    const text = `Nouvelle ligue créée sur HockeyDynasty\n\nNom: ${data.leagueName}\nCréateur: ${data.creatorUsername}\nType: ${data.leagueType}\nDate: ${now}`;
    return { html, text };
  }

  async sendAdminNewUserNotification(data: AdminNewUserEmailData): Promise<{ success: boolean; error?: string }> {
    const { html, text } = this.generateAdminNewUserEmailContent(data);
    return this.sendEmail({
      to: ['simonxcv@hotmail.com'],
      subject: `[HockeyDynasty] 👤 Nouvel utilisateur: ${data.username}`,
      html,
      text,
      tags: [{ name: 'category', value: 'admin-new-user' }],
    });
  }

  async sendAdminNewLeagueNotification(data: AdminNewLeagueEmailData): Promise<{ success: boolean; error?: string }> {
    const { html, text } = this.generateAdminNewLeagueEmailContent(data);
    return this.sendEmail({
      to: ['simonxcv@hotmail.com'],
      subject: `[HockeyDynasty] 🏆 Nouvelle ligue: ${data.leagueName}`,
      html,
      text,
      tags: [{ name: 'category', value: 'admin-new-league' }],
    });
  }

  async testEmail(testEmail: string): Promise<{ success: boolean; error?: string }> {
    const testData: ResendEmailData = {
      receiverEmail: testEmail,
      receiverUsername: 'Test User',
      senderUsername: 'Test Sender',
      tradeDetails: {
        senderAssets: [
          { name: 'Connor McDavid', type: 'player', salary: 12500000 },
          { name: 'Frederik Andersen', type: 'goalie', salary: 4500000 },
        ],
        receiverAssets: [
          { name: 'Leon Draisaitl', type: 'player', salary: 8500000 },
          { name: 'Igor Shesterkin', type: 'goalie', salary: 5666667 },
        ],
      },
      leagueName: 'Test League Dynasty',
    };

    return this.sendTradeNotification(testData);
  }

  isConfigured(): boolean {
    return !!this.supabaseAnonKey && !this.emailsDisabled;
  }
}

export const resendEmailService = new ResendEmailService();
